/**
 * Shared save-shape helpers for weight overrides.
 *
 * Three override types (per-PLU, sub-department, category) all persist the
 * same `descriptors: [{ weight, description }]` shape inside `ShopConfig.extra`.
 * The grouping overrides (sub-dept + category) additionally normalize their
 * keys to UPPER + trim and carry optional label / code / parent_subdepartment
 * fields.  These helpers centralize the cleanup so each section save path
 * stays a one-liner.
 *
 * NOTE: PLU keys remain digits-only and are NOT uppercased — only grouping
 * keys flow through `normalizeGroupingKey`.  See spec
 * `.planning/specs/2026-05-26-weight-overrides-subdept-category.md`
 * "Shared save helpers".
 */

/** Trim + uppercase a grouping key (sub-dept name or category description). */
export const normalizeGroupingKey = (value) =>
  String(value || "").trim().toUpperCase();

/** Trim weight + description on every descriptor; non-arrays collapse to []. */
export const cleanDescriptorsForSave = (descriptors) =>
  Array.isArray(descriptors)
    ? descriptors.map((descriptor) => ({
        weight: String(descriptor.weight || "").trim(),
        description: String(descriptor.description || "").trim(),
      }))
    : [];

/**
 * Build a [key, payload] tuple ready to be merged into the persisted grouping
 * override hash.  `includeParent` controls whether `parent_subdepartment` is
 * carried through (true for category overrides, false for sub-dept overrides).
 */
export const cleanGroupingOverrideForSave = ({
  key,
  label,
  code,
  parent_subdepartment,
  descriptors,
  includeParent = false,
}) => {
  const payload = {
    label: String(label || key || "").trim(),
    descriptors: cleanDescriptorsForSave(descriptors),
  };
  const cleanedCode = String(code || "").trim();
  if (cleanedCode) payload.code = cleanedCode;
  if (includeParent) {
    const parent = String(parent_subdepartment || "").trim();
    if (parent) payload.parent_subdepartment = parent;
  }
  return [normalizeGroupingKey(key || label), payload];
};
